import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PlusIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { contactHasName, normalizeContactFields } from '../lib/leadHelpers'
import { resolveValidatedAssignedTo } from '../lib/leadConversionForm'
import { logActivity, ActivityTypes } from '../utils/activityLogger'
import { CustomerCompanyFields } from '../components/customer/CustomerCompanyFields'
import { CustomerContactsFields } from '../components/customer/CustomerContactsFields'
import { CustomerManagementFields } from '../components/customer/CustomerManagementFields'
import type { CustomerContact, CustomerNote, UserProfile } from '../components/customer/types'

export default function CustomerCreate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'company' | 'contacts' | 'notes'>('company')

  // Company Information (phone/email may be prefilled from an Inbox "new contact" link)
  const [companyData, setCompanyData] = useState({
    name: '',
    phone: searchParams.get('phone') || '',
    email: searchParams.get('email') || '',
    website: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
  })

  // Contacts
  const [contacts, setContacts] = useState<CustomerContact[]>([
    {
      id: '1',
      first_name: '',
      last_name: '',
      title: '',
      phone: searchParams.get('phone') || '',
      cell: '',
      email: searchParams.get('email') || '',
    },
  ])

  // Customer Management
  const [customerData, setCustomerData] = useState({
    status: 'onboarding',
    assigned_to: '',
    payment_method: '',
    sales_tax_status: '',
  })

  const [customerNotes, setCustomerNotes] = useState<CustomerNote[]>([])
  const [customFields, setCustomFields] = useState<Record<string, any>>({})
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
    customerId?: string
  } | null>(null)
  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  // Validation and navigation
  const validateTab = (tab: 'company' | 'contacts' | 'notes'): boolean => {
    switch (tab) {
      case 'company':
        return !!companyData.name
      case 'contacts':
        return true // Contacts are optional
      case 'notes':
        return true // Notes are optional
      default:
        return false
    }
  }

  const handleNext = () => {
    if (!validateTab(activeTab)) {
      setNotification({
        type: 'error',
        message: 'Please fill in all required fields before continuing',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    const tabs: Array<'company' | 'contacts' | 'notes'> = ['company', 'contacts', 'notes']
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const tabs: Array<'company' | 'contacts' | 'notes'> = ['company', 'contacts', 'notes']
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1])
    }
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await handleSubmit({ preventDefault: () => {} } as any)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Get primary contact if exists
      const primaryContact = contacts[0] || null
      const primaryNames = primaryContact ? normalizeContactFields(primaryContact) : null

      const contactsData = contacts
        .filter((c) => contactHasName(c))
        .map((c) => {
          const names = normalizeContactFields(c)
          return {
            id: c.id,
            first_name: names.first_name,
            last_name: names.last_name,
            name: names.name,
            title: c.title || undefined,
            phone: c.phone,
            cell: c.cell,
            email: c.email,
          }
        })

      // Create customer
      const assignedTo = await resolveValidatedAssignedTo(customerData.assigned_to)
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .insert([
          {
            company_name: companyData.name || null,
            contact_first_name: primaryNames?.first_name || '',
            contact_last_name: primaryNames?.last_name || '',
            phone: companyData.phone || null,
            email: companyData.email || null,
            address: companyData.address || null,
            address_line_2: companyData.address_line_2 || null,
            city: companyData.city || null,
            state: companyData.state || null,
            zip_code: companyData.zip || null,
            status: customerData.status || 'onboarding',
            assigned_to: assignedTo,
            payment_method: customerData.payment_method || null,
            sales_tax_status: customerData.sales_tax_status || null,
            notes: customerNotes.length > 0 ? customerNotes : null,
            contacts: contactsData.length > 0 ? contactsData : null,
            custom_fields: Object.keys(customFields).length > 0 ? customFields : null,
          },
        ])
        .select()
        .single()

      if (customerError) throw customerError

      await logActivity({
        activity_type: ActivityTypes.CUSTOMER_CREATED,
        entity_type: 'customer',
        entity_id: customer.id,
        description: `Customer "${companyData.name || 'Unnamed'}" created`,
      })

      setNotification({
        type: 'success',
        message: 'Customer created successfully!',
        customerId: customer.id,
      })
      const conversationId = searchParams.get('conversationId')
      if (conversationId && customer.id) {
        try {
          await supabase
            .from('conversations')
            .update({ customer_id: customer.id })
            .eq('id', conversationId)
        } catch {
          /* non-fatal */
        }
      }
      setTimeout(() => {
        setNotification(null)
        if (conversationId) navigate(`/messages/${conversationId}?tab=external`)
        else navigate('/customers')
      }, 1500)
    } catch (error) {
      console.error('Error creating customer:', error)
      setNotification({
        type: 'error',
        message: 'Error creating customer: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
        <MobilePageHeader
          sticky
          title="Create New Customer"
          onBack={() => navigate('/customers')}
          backLabel="Back to Customers"
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab Navigation */}
          <div className={mobile.surface}>
            <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
              <div
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-6 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'company'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'contacts' || activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Company
              </div>
              <div
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-6 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'contacts'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Contacts
              </div>
              <div
                className={`flex-1 min-w-[7rem] px-4 sm:px-6 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'notes'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Notes & Management
              </div>
            </div>
            <div className="p-4 sm:p-6">
              {/* Company Tab */}
              {activeTab === 'company' && (
                <div className="space-y-6">
                  <CustomerCompanyFields value={companyData} onChange={setCompanyData} />
                  <div className="pt-6 border-t border-gray-200">
                    <CustomerManagementFields
                      management={customerData}
                      onManagementChange={setCustomerData}
                      customFields={customFields}
                      onCustomFieldsChange={setCustomFields}
                      customerNotes={customerNotes}
                      onCustomerNotesChange={setCustomerNotes}
                      users={users}
                      showNotes={false}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'contacts' && (
                <CustomerContactsFields contacts={contacts} onChange={setContacts} />
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <CustomerManagementFields
                  management={customerData}
                  onManagementChange={setCustomerData}
                  customFields={customFields}
                  onCustomFieldsChange={setCustomFields}
                  customerNotes={customerNotes}
                  onCustomerNotesChange={setCustomerNotes}
                  users={users}
                  showManagement={false}
                  showCustomFields={false}
                  showNotes
                />
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:rounded-xl lg:border lg:border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className={mobile.secondaryBtn}
            >
              Cancel
            </button>

            <div className="flex gap-2 sm:gap-3">
              {activeTab !== 'company' && (
                <button
                  type="button"
                  onClick={handleBack}
                  className={mobile.secondaryBtn}
                >
                  Back
                </button>
              )}
              {activeTab === 'notes' ? (
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={loading}
                  className={mobile.primaryBtn}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-4 h-4" />
                      <span className="sm:hidden">Create</span>
                      <span className="hidden sm:inline">Create Customer</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!validateTab(activeTab)}
                  className={mobile.primaryBtn}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </form>

      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-md w-full mx-0 sm:mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-semibold ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
                {notification.customerId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/customers/view/${notification.customerId}`)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View Customer
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
